import { formatDollars } from '@fruitful/fruitils';
import { scaleLinear } from 'd3-scale';
import { area, curveMonotoneX, curveStepAfter, line } from 'd3-shape';
import * as Haptics from 'expo-haptics';
import {
    useCallback,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    type GestureResponderEvent,
    type LayoutChangeEvent,
    Platform,
    useWindowDimensions,
    View as RNView,
    type ViewStyle,
} from 'react-native';
import Svg, {
    Circle,
    Line,
    LinearGradient,
    Path,
    Rect,
    Stop,
} from 'react-native-svg';
import {
    BadgeLabel,
    BodyLargeEmphasized,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    type ColorTokenKey,
    LabelRegular,
    MinusIcon,
    PlusIcon,
    useThemeColors,
    View,
    XStack,
    YStack,
} from 'smoothie';

import {
    format as formatDate,
    SHORT_READABLE_DATE_FORMAT,
} from '../../../common/utils/dateUtils';
import {
    APEX_PERFORMANCE_HORIZONS,
    type ApexAccountPerformanceSeriesItem,
    type ApexPerformanceDatapoint,
    ApexPerformanceHorizon,
} from '../../types/performance';

const ACCOUNT_VALUE_LINE_COLOR: ColorTokenKey = '$green1100';
const NET_CONTRIBUTION_LINE_COLOR: ColorTokenKey = '$green900';
const MARKER_LINE_COLOR: ColorTokenKey = '$gray1200';
const GRADIENT_START_COLOR: ColorTokenKey = '$green400';

const STROKE_WIDTH = 2;
const CURVED_MARKER_RADIUS = 5;
const STEPPED_MARKER_SIZE = CURVED_MARKER_RADIUS * 2;
const STEPPED_MARKER_RX = Math.min(2, STEPPED_MARKER_SIZE / 2);

const CHART_HORIZONTAL_OUTSET = Math.ceil(
    Math.max(
        CURVED_MARKER_RADIUS + STROKE_WIDTH,
        STEPPED_MARKER_SIZE / 2 + STROKE_WIDTH,
    ) + 2,
);

export type ApexPerformanceSeriesLegendVariant =
    | 'accountValuation'
    | 'netContribution';

export interface ApexAccountPerformanceGraphProps {
    backgroundColor?: ColorTokenKey;
    datapoints?: ApexPerformanceDatapoint[];
    onActiveDatapointChange?: (
        datapoint: ApexPerformanceDatapoint | null,
    ) => void;
    onScrollLock?: (locked: boolean) => void;
}

const clamp = (value: number, min: number, max: number) =>
    Math.min(max, Math.max(min, value));

const readNumericField = (
    nativeEvent: object,
    key: 'offsetX' | 'locationX',
): number | null => {
    if (
        key in nativeEvent &&
        typeof (nativeEvent as Record<string, unknown>)[key] === 'number' &&
        !Number.isNaN((nativeEvent as Record<string, number>)[key])
    ) {
        return (nativeEvent as Record<string, number>)[key] as number;
    }
    return null;
};

const getPointerOffsetX = (nativeEvent: object): number | null =>
    readNumericField(nativeEvent, 'offsetX') ??
    readNumericField(nativeEvent, 'locationX');

export const ApexPerformanceBadges = ({
    performanceSeries,
}: {
    performanceSeries: ApexAccountPerformanceSeriesItem | null;
}) => {
    if (performanceSeries == null) return null;

    const { amountReturn, percentageReturn } = performanceSeries;
    const isPositive = amountReturn >= 0;

    return (
        <XStack gap={4} flexWrap="wrap">
            <BadgeLabel
                variant={isPositive ? 'positive' : 'warning'}
                icon={isPositive ? PlusIcon : MinusIcon}
                text={formatDollars(Math.abs(amountReturn))}
                size="regular"
            />
            {percentageReturn != null && (
                <BadgeLabel
                    variant="neutral"
                    text={`${percentageReturn.toFixed(1)}% returns`}
                    size="regular"
                />
            )}
        </XStack>
    );
};

export const ApexSnapshotRow = ({
    performanceSnapshot,
}: {
    performanceSnapshot: ApexPerformanceDatapoint;
}) => {
    if (performanceSnapshot == null) return null;

    return (
        <YStack gap={12}>
            <LabelRegular color="$gray1000">
                {formatDate(
                    performanceSnapshot.step,
                    SHORT_READABLE_DATE_FORMAT,
                )}
            </LabelRegular>

            <XStack justifyContent="space-between" alignItems="center">
                <YStack>
                    <XStack alignItems="center" gap={6}>
                        <ApexPerformanceSeriesLegendIcon variant="accountValuation" />
                        <BodySmallRegular>Account value</BodySmallRegular>
                    </XStack>
                    <BodyLargeEmphasized>
                        {formatDollars(performanceSnapshot.accountValuation)}
                    </BodyLargeEmphasized>
                </YStack>

                <YStack alignItems="flex-end">
                    <XStack alignItems="center" gap={6}>
                        <ApexPerformanceSeriesLegendIcon variant="netContribution" />
                        <BodySmallRegular>Net contributions</BodySmallRegular>
                    </XStack>
                    <BodyLargeEmphasized>
                        {formatDollars(performanceSnapshot.netContribution)}
                    </BodyLargeEmphasized>
                </YStack>
            </XStack>
        </YStack>
    );
};

const AccountValueMarker = ({ x, y }: { x: number; y: number }) => {
    const { getColor } = useThemeColors();
    const white = getColor('$white');
    const primary = getColor(ACCOUNT_VALUE_LINE_COLOR);

    return (
        <Circle
            cx={x}
            cy={y}
            fill={white}
            r={CURVED_MARKER_RADIUS}
            stroke={primary}
            strokeWidth={STROKE_WIDTH}
            pointerEvents="none"
        />
    );
};

const NetContributionMarker = ({ x, y }: { x: number; y: number }) => {
    const { getColor } = useThemeColors();
    const white = getColor('$white');
    const secondary = getColor(NET_CONTRIBUTION_LINE_COLOR);

    return (
        <Rect
            fill={white}
            height={STEPPED_MARKER_SIZE}
            rx={STEPPED_MARKER_RX}
            ry={STEPPED_MARKER_RX}
            stroke={secondary}
            strokeWidth={STROKE_WIDTH}
            width={STEPPED_MARKER_SIZE}
            x={x}
            y={y}
            pointerEvents="none"
        />
    );
};

export const ApexPerformanceSeriesLegendIcon = ({
    variant,
}: {
    variant: ApexPerformanceSeriesLegendVariant;
}) => {
    const LEGEND_ICON_WIDTH = 24;
    const LEGEND_ICON_HEIGHT = 18;
    const LEGEND_CENTER_X = LEGEND_ICON_WIDTH / 2;
    const LEGEND_CENTER_Y = LEGEND_ICON_HEIGHT / 2;
    const LEGEND_LINE_HALF_LEN = 12;

    const isAccountValuation = variant === 'accountValuation';

    const { getColor } = useThemeColors();
    const strokeColor = getColor(
        isAccountValuation
            ? ACCOUNT_VALUE_LINE_COLOR
            : NET_CONTRIBUTION_LINE_COLOR,
    );

    return (
        <Svg
            height={LEGEND_ICON_HEIGHT}
            viewBox={`0 0 ${LEGEND_ICON_WIDTH} ${LEGEND_ICON_HEIGHT}`}
            width={LEGEND_ICON_WIDTH}
        >
            <Line
                stroke={strokeColor}
                strokeDasharray={isAccountValuation ? '0 0' : '3 4'}
                strokeLinecap="round"
                strokeWidth={2}
                x1={LEGEND_CENTER_X - LEGEND_LINE_HALF_LEN}
                x2={LEGEND_CENTER_X + LEGEND_LINE_HALF_LEN}
                y1={LEGEND_CENTER_Y}
                y2={LEGEND_CENTER_Y}
            />
            {isAccountValuation ? (
                <AccountValueMarker x={LEGEND_CENTER_X} y={LEGEND_CENTER_Y} />
            ) : (
                <NetContributionMarker
                    x={LEGEND_CENTER_X - STEPPED_MARKER_SIZE / 2}
                    y={LEGEND_CENTER_Y - STEPPED_MARKER_SIZE / 2}
                />
            )}
        </Svg>
    );
};

const HorizonSelectionControl = ({
    horizon,
    isSelected,
    onPress,
}: {
    horizon: string;
    isSelected: boolean;
    onPress: () => void;
}) => {
    return (
        <Button
            text={horizon}
            paddingHorizontal={12}
            borderRadius={32}
            width="auto"
            variant={
                isSelected ? ButtonVariant.PRIMARY : ButtonVariant.TERTIARY
            }
            size={ButtonSize.TINY}
            onPress={onPress}
        />
    );
};

export const ApexAccountHorizonSelectionRow = ({
    selectedPerformanceHorizon,
    onSelect,
}: {
    selectedPerformanceHorizon: ApexPerformanceHorizon;
    onSelect: (horizon: ApexPerformanceHorizon) => void;
}) => {
    return (
        <XStack
            alignItems="center"
            justifyContent="space-between"
            paddingTop={8}
        >
            {APEX_PERFORMANCE_HORIZONS.map((horizon) => (
                <View key={horizon} flex={1}>
                    <HorizonSelectionControl
                        horizon={
                            horizon === ApexPerformanceHorizon.ALL
                                ? 'All'
                                : horizon
                        }
                        isSelected={selectedPerformanceHorizon === horizon}
                        onPress={() => {
                            onSelect(horizon);
                        }}
                    />
                </View>
            ))}
        </XStack>
    );
};

/**
 * Collapses consecutive same-y points so d3's curveStepAfter never receives
 * duplicate y values in sequence, eliminating the zero-length vertical segments
 * that cause strokeDasharray to visually compress on native SVG.
 * The last original point is always included to preserve the full x-extent of
 * the final flat run.
 */
const collapseSteppedSeries = (
    series: Array<[number, number]>,
): Array<[number, number]> => {
    const first = series[0];
    if (first == null) return [];

    const result: Array<[number, number]> = [first];
    for (let i = 1; i < series.length; i++) {
        const current = series[i];
        const previous = series[i - 1];
        if (current == null || previous == null) continue;
        if (current[1] !== previous[1]) {
            result.push(current);
        }
    }
    const last = series[series.length - 1];
    if (last != null && result[result.length - 1] !== last) {
        result.push(last);
    }
    return result;
};

export const ApexAccountPerformanceGraph = ({
    backgroundColor = '$white',
    datapoints,
    onActiveDatapointChange,
    onScrollLock,
}: ApexAccountPerformanceGraphProps) => {
    if (datapoints == null || datapoints.length < 2) return null;

    const GRAPH_HEIGHT = 225;
    const GRAPH_MARGIN = 0;
    const GRAPH_VERTICAL_MARGIN = 12;

    const curvedAreaGradientId = `apexPerfCurvedArea-${useId().replace(/:/g, '')}`;

    const [containerWidth, setContainerWidth] = useState(0);
    const [activeIndex, setActiveIndex] = useState<number | null>(null);
    const lastScrubIndexForHapticsRef = useRef<number | null>(null);

    const { getColor } = useThemeColors();
    const { width: windowWidth } = useWindowDimensions();

    const areaGradientStartColor = getColor(GRADIENT_START_COLOR);
    const areaGradientEndColor = getColor(backgroundColor);
    const primaryLineColor = getColor(ACCOUNT_VALUE_LINE_COLOR);
    const secondaryLineColor = getColor(NET_CONTRIBUTION_LINE_COLOR);
    const crosshairColor = getColor(MARKER_LINE_COLOR);

    useEffect(() => {
        lastScrubIndexForHapticsRef.current = null;
        setActiveIndex(null);
    }, [datapoints]);

    useEffect(() => {
        onActiveDatapointChange?.(
            activeIndex != null && datapoints[activeIndex] != null
                ? datapoints[activeIndex]
                : null,
        );
    }, [activeIndex, datapoints, onActiveDatapointChange]);

    const onContainerLayout = useCallback((event: LayoutChangeEvent) => {
        const { width } = event.nativeEvent.layout;
        if (width > 0) {
            setContainerWidth(width);
        }
    }, []);

    const chartWidth = Math.max(
        containerWidth > 0 ? containerWidth : windowWidth,
        1,
    );

    const { steppedSeries, curvedSeries } = useMemo(() => {
        return {
            steppedSeries: datapoints.map(
                (p, i) => [i, p.netContribution] as [number, number],
            ),
            curvedSeries: datapoints.map(
                (p, i) => [i, p.accountValuation] as [number, number],
            ),
        };
    }, [datapoints]);

    const {
        steppedPathD,
        curvedAreaD,
        curvedLineD,
        xScale,
        yScale,
        plotTop,
        plotBottom,
        pointCount,
    } = useMemo(() => {
        const innerWidth = chartWidth - 2 * GRAPH_MARGIN;
        const innerHeight = GRAPH_HEIGHT - 2 * GRAPH_VERTICAL_MARGIN;
        const plotTopY = GRAPH_VERTICAL_MARGIN;
        const plotBottomY = GRAPH_VERTICAL_MARGIN + innerHeight;

        if (steppedSeries.length === 0) {
            return {
                steppedPathD: '',
                curvedAreaD: '',
                curvedLineD: '',
                xScale: scaleLinear().domain([0, 1]).range([0, 0]),
                yScale: scaleLinear().domain([0, 1]).range([0, 0]),
                plotTop: plotTopY,
                plotBottom: plotBottomY,
                pointCount: 0,
            };
        }

        const xs = [
            ...steppedSeries.map((d) => d[0]),
            ...curvedSeries.map((d) => d[0]),
        ];
        const ys = [
            ...steppedSeries.map((d) => d[1]),
            ...curvedSeries.map((d) => d[1]),
        ];
        const xMin = Math.min(...xs);
        const xMax = Math.max(...xs);
        const yMin = Math.min(0, ...ys);
        const yMax = Math.max(1, ...ys);

        const xScale0 = scaleLinear()
            .domain([xMin, xMax])
            .range([
                GRAPH_MARGIN + CHART_HORIZONTAL_OUTSET,
                GRAPH_MARGIN + innerWidth + CHART_HORIZONTAL_OUTSET,
            ]);

        const yScale = scaleLinear()
            .domain([yMin, yMax])
            .range([plotBottomY, plotTopY]);

        const baselineY = plotBottomY;

        const steppedLineGenerator = line<[number, number]>()
            .x((d) => xScale0(d[0]) ?? 0)
            .y((d) => yScale(d[1]) ?? 0)
            .curve(curveStepAfter);

        const curvedLineGenerator = line<[number, number]>()
            .x((d) => xScale0(d[0]) ?? 0)
            .y((d) => yScale(d[1]) ?? 0)
            .curve(curveMonotoneX);

        const curvedAreaGenerator = area<[number, number]>()
            .x((d) => xScale0(d[0]) ?? 0)
            .y0(baselineY)
            .y1((d) => yScale(d[1]) ?? 0)
            .curve(curveMonotoneX);

        return {
            steppedPathD:
                steppedLineGenerator(collapseSteppedSeries(steppedSeries)) ??
                '',
            curvedAreaD: curvedAreaGenerator(curvedSeries) ?? '',
            curvedLineD: curvedLineGenerator(curvedSeries) ?? '',
            xScale: xScale0,
            yScale,
            plotTop: plotTopY,
            plotBottom: plotBottomY,
            pointCount: steppedSeries.length,
        };
    }, [chartWidth, steppedSeries, curvedSeries]);

    const nearestIndexFromOffsetX = useCallback(
        (offsetX: number) => {
            if (pointCount < 1) {
                return null;
            }
            if (pointCount === 1) {
                return 0;
            }
            const innerWidth = chartWidth - 2 * GRAPH_MARGIN;
            if (innerWidth <= 0) {
                return null;
            }
            const rel = clamp((offsetX - GRAPH_MARGIN) / innerWidth, 0, 1);
            return Math.round(rel * (pointCount - 1));
        },
        [chartWidth, pointCount],
    );

    const updatePointer = useCallback(
        (nativeEvent: object) => {
            const offsetX = getPointerOffsetX(nativeEvent);
            if (offsetX == null) {
                return;
            }
            const nextIndex = nearestIndexFromOffsetX(offsetX);
            const prevIndex = lastScrubIndexForHapticsRef.current;
            if (
                Platform.OS !== 'web' &&
                nextIndex != null &&
                nextIndex !== prevIndex
            ) {
                void Haptics.selectionAsync().catch(() => {});
            }
            lastScrubIndexForHapticsRef.current = nextIndex;
            setActiveIndex(nextIndex);
        },
        [nearestIndexFromOffsetX],
    );

    const clearPointer = useCallback(() => {
        lastScrubIndexForHapticsRef.current = null;
        setActiveIndex(null);
        if (Platform.OS !== 'web') {
            onScrollLock?.(false);
        }
    }, [onScrollLock]);

    const updateFromTouch = useCallback(
        (event: GestureResponderEvent) => {
            updatePointer(event.nativeEvent);
        },
        [updatePointer],
    );

    const handleTouchStart = useCallback(
        (event: GestureResponderEvent) => {
            if (Platform.OS !== 'web') {
                onScrollLock?.(true);
            }
            updateFromTouch(event);
        },
        [onScrollLock, updateFromTouch],
    );

    const crosshairX =
        activeIndex != null && pointCount > 0 ? xScale(activeIndex) : null;

    const activeDatum =
        activeIndex != null ? datapoints[activeIndex] : undefined;
    const curvedIntersectionY =
        crosshairX != null && activeDatum != null
            ? yScale(activeDatum.accountValuation)
            : null;
    const steppedIntersectionY =
        crosshairX != null && activeDatum != null
            ? yScale(activeDatum.netContribution)
            : null;

    const svgWidth = chartWidth + 2 * CHART_HORIZONTAL_OUTSET;

    return (
        <View
            alignSelf="stretch"
            height={GRAPH_HEIGHT}
            overflow="visible"
            position="relative"
            width="100%"
            onLayout={onContainerLayout}
        >
            <RNView
                pointerEvents="none"
                style={{
                    height: GRAPH_HEIGHT,
                    left: -CHART_HORIZONTAL_OUTSET,
                    overflow: 'visible',
                    position: 'absolute',
                    top: 0,
                    width: svgWidth,
                }}
            >
                <Svg
                    height={GRAPH_HEIGHT}
                    viewBox={`0 0 ${svgWidth} ${GRAPH_HEIGHT}`}
                    width={svgWidth}
                >
                    <LinearGradient
                        id={curvedAreaGradientId}
                        x1="0%"
                        y1="0%"
                        x2="0%"
                        y2="100%"
                        gradientUnits="objectBoundingBox"
                    >
                        <Stop offset="0" stopColor={areaGradientStartColor} />
                        <Stop offset="1" stopColor={areaGradientEndColor} />
                    </LinearGradient>
                    <Path
                        d={curvedAreaD}
                        fill={`url(#${curvedAreaGradientId})`}
                        stroke="none"
                    />
                    {/* Account Value */}
                    <Path
                        d={curvedLineD}
                        fill="none"
                        stroke={primaryLineColor}
                        strokeWidth={STROKE_WIDTH}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                    {/* Net Contributions */}
                    <Path
                        d={steppedPathD}
                        fill="none"
                        stroke={secondaryLineColor}
                        strokeWidth={STROKE_WIDTH}
                        strokeDasharray="4 4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                    {/* Hover Axis */}
                    {crosshairX != null && (
                        <Line
                            x1={crosshairX}
                            x2={crosshairX}
                            y1={plotTop}
                            y2={plotBottom}
                            stroke={crosshairColor}
                            strokeOpacity={0.5}
                            strokeWidth={1}
                            strokeDasharray="2 2"
                            pointerEvents="none"
                        />
                    )}
                    {crosshairX != null && curvedIntersectionY != null && (
                        <AccountValueMarker
                            x={crosshairX}
                            y={curvedIntersectionY}
                        />
                    )}
                    {crosshairX != null && steppedIntersectionY != null && (
                        <NetContributionMarker
                            x={crosshairX - STEPPED_MARKER_SIZE / 2}
                            y={steppedIntersectionY - STEPPED_MARKER_SIZE / 2}
                        />
                    )}
                </Svg>
            </RNView>
            {/*
              RN iOS/Android: use classic touch events + raw RN View so hits register
              reliably (pointer events are inconsistent; Tamagui View may not forward touches).
              Web: pointer events for hover; touch events for mobile web.
            */}
            <RNView
                collapsable={false}
                style={[
                    {
                        height: GRAPH_HEIGHT,
                        left: 0,
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        zIndex: 1,
                    },
                    Platform.OS === 'web'
                        ? ({ cursor: 'crosshair' } as unknown as ViewStyle)
                        : undefined,
                ]}
                onPointerDown={(e) => {
                    updatePointer(e.nativeEvent);
                }}
                onPointerLeave={clearPointer}
                onPointerMove={(e) => {
                    updatePointer(e.nativeEvent);
                }}
                onTouchCancel={clearPointer}
                onTouchEnd={clearPointer}
                onTouchMove={updateFromTouch}
                onTouchStart={handleTouchStart}
            />
        </View>
    );
};
